import { FieldGroup } from '@/ui'
import { useTranslation } from 'react-i18next'

import { Button, Modal, ModalContent, ModalFooter, ModalProps, ModalTitle, Select } from '@dashboard/ui'
import { useStoreItemsQuery } from '@/layouts/store/api/useStoreItemsQuery'
import { useGenerateCompanyPath } from '@/libs'
import { useEffect, useState } from 'react'
import { useStoresQuery } from '@/layouts/store/api'
import { dashboardClient } from '@/api'
import { DEFAULT_IMAGE_PLACEHOLDER_ITEM } from '@/Settings'
import { PriceView } from '@/layouts/game-items/components/PriceView'
import { getItemName } from '@/api/getItemName'

// @ts-ignore
export interface SelectStoreItemModalProps extends ModalProps {
  onChange: (value: string | undefined) => void
  value: string | undefined
}

export const SelectStoreItemModal = ({ onChange, value, ...rest }: SelectStoreItemModalProps) => {
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: stores = [] } = useStoresQuery(companyId, gameId, { limit: 100 })

  const [storeId, setStoreId] = useState<string | null>(null)
  const [storeItemId, setStoreItemId] = useState<string | undefined>(value)

  const { data: storeItems = [] } = useStoreItemsQuery(companyId, gameId, storeId!)

  useEffect(() => {
    const loadStore = async () => {
      if (!value) {
        return
      }
      const { data: store } = await dashboardClient.v1.getStoreByStoreItem(value, companyId, gameId)
      setStoreId(store.id)
    }

    loadStore()
  }, [value])

  const applyClick = () => {
    onChange(storeItemId)
    rest.onClose?.()
  }

  let options = storeItems.map(it => {
    return {
      value: it.id,
      icon: () => (
        <div
          className="size-[28px] shrink-0 rounded-md bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${it.item.image_url || DEFAULT_IMAGE_PLACEHOLDER_ITEM})`,
          }}
        />
      ),
      caption: (
        <div className="max-w-[256px] truncate text-xs font-medium leading-none text-text-quarterary-hover">
          {it.item.sku}
        </div>
      ),
      extra: () => {
        return it.is_free_item ? <> {t('store.item.free_item')} </> : <PriceView item={it.item} />
      },
      children: getItemName(it.item),
    }
  })

  return (
    <Modal {...rest} size="lg">
      <ModalContent>
        <ModalTitle>{t('edit-list')}</ModalTitle>

        <FieldGroup label={t('store.store')}>
          <Select
            value={storeId}
            onChange={v => setStoreId(v as string)}
            items={stores.map(s => ({
              value: s.id,
              children: s.name,
            }))}
          />
        </FieldGroup>

        <FieldGroup label={t('store.store-item')}>
          <Select
            items={options}
            placeholder={t('select-skus')}
            value={storeItemId}
            onChange={v => setStoreItemId(v as string)}
          />
        </FieldGroup>
      </ModalContent>

      <ModalFooter>
        <Button variant="outline" size="sm" color="secondary" onClick={rest.onClose}>
          {t('Cancel')}
        </Button>
        <Button size="sm" onClick={applyClick}>
          {t('Apply')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
